import React, { MouseEvent } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Tooltip } from '@ui/Tooltip';
import { PopoverPosition } from '@mui/material';

export type DragTooltipItem = {
  id: string;
  label: string;
  area: string;
  percent: string;
};

export type DragTooltipState = {
  anchorPosition: PopoverPosition;
  items: DragTooltipItem[];
};

type ActivityRefDragHandleProps = {
  dragTooltip: DragTooltipState | null;
  isDragging: boolean;
  index: number;
  registerHandle: (index: number) => (element: HTMLDivElement | null) => void;
  onMouseLeave?: (event?: MouseEvent) => void;
  selected?: boolean;
};

/**
 * Draghandtag for att justera andelar for aktivitetsreferenser.
 * Visar tooltip med aktivitetsdetaljer (etikett, yta, procent) vid drag.
 */
export const ActivityRefDragHandle: React.FC<ActivityRefDragHandleProps> = ({
  dragTooltip,
  isDragging,
  index,
  registerHandle,
  onMouseLeave,
  selected,
}) => {
  const { classes, cx } = useStyles();

  return (
    <Tooltip
      title={
        <>
          {/* Bygger tooltipinnehall utifran aktuell dragdata */}
          {dragTooltip
            ? dragTooltip.items.map((item) => (
                <div key={item.id} className={classes.dragTooltipRow}>
                  <span className={classes.dragTooltipLabel}>{item.label}</span>
                  <span>{item.area}</span>
                  <span>{item.percent}</span>
                </div>
              ))
            : 'Adjust activity area'}
        </>
      }
      open={isDragging ? true : false}
    >
      <div
        className={cx(classes.handleGap, selected && classes.handleGapSelected)}
      >
        <div
          ref={registerHandle(index)}
          className={classes.handleHitArea}
          role="separator"
          aria-label="Adjust activity share"
          onMouseLeave={onMouseLeave}
        />
      </div>
    </Tooltip>
  );
};

const useStyles = makeStyles()((theme) => ({
  handleGap: {
    position: 'relative',
    width: '1px',
    backgroundColor: theme.palette.common.white,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  handleGapSelected: {
    backgroundColor: theme.palette.common.white,
  },
  handleHitArea: {
    position: 'absolute',
    left: `calc(50% - ${theme.spacing(1.5)})`,
    width: theme.spacing(3),
    top: 0,
    bottom: 0,
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    zIndex: 2,
  },
  dragTooltipRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: theme.typography.caption.fontSize,
    whiteSpace: 'nowrap',
  },
  dragTooltipLabel: {
    fontWeight: 600,
  },
}));
